import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useBreakpoints } from "~/hooks/useBreakpoints";

import { SheetModal } from "../sheetModal/SheetModal";
import { ToastStack } from "../toast/ToastStack";
import { useToasts } from "../toast/useToasts";
import { Text } from "../text/Text";
import { DialogModal } from "../dialogModal/DialogModal";

import type { CSSProperties } from "react";
import type { QuizViewQuestionFeedbackMessage } from "./QuizViewManager";

export interface QuizViewMessagesRef {
  hideAll: () => void;
  hideErrorMessage: () => void;
  hideSuccessMessage: () => void;
  showErrorMessage: (message: QuizViewQuestionFeedbackMessage) => void;
  showSuccessMessage: (message: QuizViewQuestionFeedbackMessage) => void;
}

export const QuizViewMessages = forwardRef<QuizViewMessagesRef>(
  function QuizViewMessages(_, ref) {
    const containerRef = useRef<HTMLDivElement>(null);

    const [successMessage, setSuccessMessage] =
      useState<QuizViewQuestionFeedbackMessage | null>(null);

    const { showToast, dismissAllToasts } = useToasts();

    const breakpoints = useBreakpoints();

    const insetBottom =
      "calc(var(--size-8) + max(var(--safe-area-inset-bottom), var(--size-2)))";

    const sheetCustomProperties = {
      "--sheet-modal-inset-bottom": insetBottom,
    } as CSSProperties;

    const handleRequestClose = useCallback(() => {
      setSuccessMessage(null);
    }, []);

    const content = useMemo(() => {
      return (
        <>
          <Text weight="bold" size="lg">
            {successMessage?.title}
          </Text>

          <Text as="p">
            <span
              dangerouslySetInnerHTML={{
                __html: successMessage?.description ?? "",
              }}
            />
          </Text>
        </>
      );
    }, [successMessage?.description, successMessage?.title]);

    // Define handlers
    useImperativeHandle(ref, () => ({
      hideAll() {
        dismissAllToasts();
        setSuccessMessage(null);
      },
      hideErrorMessage() {
        dismissAllToasts();
      },
      hideSuccessMessage() {
        setSuccessMessage(null);
      },
      showErrorMessage(message) {
        showToast({
          title: message.title,
          description: message.description,
          intent: "error",
          dismissable: false,
        });
      },
      showSuccessMessage(message) {
        setSuccessMessage(message);
      },
    }));

    return (
      <div ref={containerRef} className="QuizViewMessages">
        {breakpoints.lg ? (
          <DialogModal
            isOpen={Boolean(successMessage)}
            size="sm"
            onRequestClose={handleRequestClose}
          >
            <DialogModal.Body className="QuizViewMessages__Modal">
              {content}
            </DialogModal.Body>
          </DialogModal>
        ) : (
          <SheetModal
            isOpen={Boolean(successMessage)}
            style={sheetCustomProperties}
            root={containerRef.current}
            onRequestClose={handleRequestClose}
          >
            <SheetModal.Body className="QuizViewMessages__Modal">
              {content}
            </SheetModal.Body>
          </SheetModal>
        )}

        <ToastStack insetBottom={insetBottom} />
      </div>
    );
  },
);
